import React from "react";
import { X, Loader2, Mail } from "lucide-react";

/**
 * CreateZohoTicketModal — extracted from ChatsPage.jsx (iter 156.2, Feb 2026).
 *
 * Creates a Zoho Desk ticket from a WhatsApp chat. Internal mode → emails
 * help@exoticindia.com (or override). Customer mode → opens a ticket against
 * the lead's email so Zoho threads the customer's replies back.
 */
export const CreateZohoTicketModal = ({
  open,
  onClose,
  selectedLead,
  ticketInternal,
  setTicketInternal,
  ticketEmailOverride,
  setTicketEmailOverride,
  ticketSubject,
  setTicketSubject,
  ticketDescription,
  setTicketDescription,
  creatingTicket,
  onCreate,
}) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="create-ticket-from-chat-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[--border]">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center"><Mail size={16} /></span>
            <h2 className="text-sm font-semibold" style={{ fontFamily: "Playfair Display, serif" }}>Create Email Ticket</h2>
          </div>
          <button onClick={onClose} className="btn-icon"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-lg p-2 text-amber-800">
            {ticketInternal
              ? <>Internal ticket — routed to <b>{ticketEmailOverride.trim() || "help@exoticindia.com"}</b>. The customer won't see this thread. Customer context (name / phone / lead) will be included automatically.</>
              : <>Creates a Zoho ticket for <b>{selectedLead?.name || selectedLead?.phone}</b>{selectedLead?.customer_email ? ` (${selectedLead.customer_email})` : " (no email on file)"}. Auto-linked to this lead.</>
            }
          </div>

          {/* Internal / Customer toggle */}
          <div className="flex items-center gap-2 p-2 rounded-xl border border-[--border] bg-[--bg-cream]" data-testid="ticket-mode-toggle">
            <button
              type="button"
              onClick={() => setTicketInternal(true)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all ${ticketInternal ? "bg-white text-amber-700 shadow-sm border border-amber-200" : "text-[--text-muted] hover:text-[--text-primary]"}`}
              data-testid="ticket-mode-internal-btn"
            >
              🏢 Internal
            </button>
            <button
              type="button"
              onClick={() => setTicketInternal(false)}
              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all ${!ticketInternal ? "bg-white text-blue-700 shadow-sm border border-blue-200" : "text-[--text-muted] hover:text-[--text-primary]"}`}
              data-testid="ticket-mode-customer-btn"
            >
              👤 Customer
            </button>
          </div>

          {ticketInternal && (
            <div>
              <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide block mb-1">Send to (email)</label>
              <input
                type="email"
                value={ticketEmailOverride}
                onChange={(e) => setTicketEmailOverride(e.target.value)}
                placeholder="help@exoticindia.com"
                className="w-full border-2 border-[--border] rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-300 font-mono"
                data-testid="ticket-internal-email-input"
              />
              <p className="text-[9px] text-[--text-muted] mt-1">Defaults to <b>help@exoticindia.com</b>. Edit to route to a specific mailbox / agent.</p>
            </div>
          )}

          <div>
            <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide block mb-1">Subject</label>
            <input
              value={ticketSubject}
              onChange={(e) => setTicketSubject(e.target.value)}
              className="w-full border-2 border-[--border] rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-300"
              data-testid="ticket-subject-input"
            />
          </div>
          <div>
            <label className="text-[11px] font-bold text-[--text-muted] uppercase tracking-wide block mb-1">Description</label>
            <textarea
              rows={6}
              value={ticketDescription}
              onChange={(e) => setTicketDescription(e.target.value)}
              className="w-full border-2 border-[--border] rounded-xl px-3 py-2 text-xs outline-none focus:border-amber-300 resize-y"
              data-testid="ticket-description-input"
            />
          </div>
          {!ticketInternal && !selectedLead?.customer_email && (
            <p className="text-[10px] text-red-600">⚠ No email on lead — create will fail. Add customer email first via Details panel, or switch to Internal.</p>
          )}
          <button
            onClick={onCreate}
            disabled={creatingTicket || (!ticketInternal && !selectedLead?.customer_email)}
            className="w-full py-2.5 rounded-full text-sm font-bold text-white flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: "linear-gradient(135deg, #D97706, #F59E0B)" }}
            data-testid="confirm-create-ticket-btn"
          >
            {creatingTicket ? <Loader2 size={14} className="animate-spin" /> : <><Mail size={14} /> Create {ticketInternal ? "Internal " : ""}Ticket</>}
          </button>
        </div>
      </div>
    </div>
  );
};
